import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { useTheme, THEMES, THEME_LABELS } from '../../context/ThemeContext';
import ThemeModeIcon from '../ThemeModeIcon/ThemeModeIcon';
import { getBaseApiUrl } from '../../config/api';
import { isPathbuddyHost } from '../../config/eduHost';
import useStaffDashboardAccess from '../../features/organisations/hooks/useStaffDashboardAccess';
import { openCookieSettings } from '../../features/consent/consentStore';
import './UserProfileDropdown.css';


/* ─── tiny inline modal ─────────────────────────────────────────────────── */
function InlineModal({ isOpen, onClose, title, subtitle, placeholder, buttonLabel, onSubmit, sending, sent, extraContent }) {
  const [text, setText] = useState('');

  const handleSubmit = () => {
    if (!text.trim()) return;
    onSubmit(text);
  };

  const handleClose = () => {
    setText('');
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="upd-modal-backdrop" onClick={handleClose}>
      <div className="upd-modal" onClick={e => e.stopPropagation()}>
        <button className="upd-modal-close" onClick={handleClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <h3 className="upd-modal-title">{title}</h3>
        {subtitle && <p className="upd-modal-subtitle">{subtitle}</p>}
        {sent ? (
          <div className="upd-modal-sent">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--iv-success)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <p>Message sent! We'll get back to you soon.</p>
          </div>
        ) : (
          <>
            <div className="upd-modal-field">
              <label className="upd-modal-field-label">{placeholder.split('…')[0]}</label>
              <textarea
                className="upd-modal-textarea"
                placeholder={placeholder}
                value={text}
                onChange={e => setText(e.target.value)}
                rows={5}
                autoFocus
              />
            </div>
            {extraContent}
            <div className="upd-modal-actions">
              <button className="upd-modal-btn upd-modal-btn--cancel" onClick={handleClose}>Cancel</button>
              <button
                className="upd-modal-btn upd-modal-btn--submit"
                onClick={handleSubmit}
                disabled={sending || !text.trim()}
              >
                {sending ? 'Sending…' : buttonLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
/* ────────────────────────────────────────────────────────────────────────── */

const UserProfileDropdown = ({ userName, userEmail, variant }) => {
  const { user, logout} = useAuth();

  const displayInitials = (() => {
    const name = (userName || user?.name || '').trim();

    if (name) {
      const names = name.split(/\s+/);
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return names[0][0].toUpperCase();
    }

    const email = userEmail || user?.email || '';
    return email ? email[0].toUpperCase() : 'U';
  })();

  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAppsOpen, setIsAppsOpen] = useState(false);
  const dropdownRef = useRef(null);

  /* modal state */
  const [contactOpen, setContactOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [bugSending, setBugSending] = useState(false);
  const [bugSent, setBugSent] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
        setIsAppsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/');
    }
  };

  const handleProfileClick = () => {
    setIsProfileOpen(false);
    navigate('/profile');
  };

  const handleInstructorDashboardClick = () => {
    setIsProfileOpen(false);
    navigate('/teacher-dashboard');
  };

  const showInstructorDashboard = useStaffDashboardAccess(user?.id);

  const handleAppNav = (path) => {
    setIsAppsOpen(false);
    navigate(path);
  };

  /* ── email send helper ── */
  const onPathbuddy = isPathbuddyHost();
  const brandLabel = onPathbuddy ? 'Pathbuddy' : 'ivori';
  const supportEmail = onPathbuddy ? 'hello@pathbuddy.com' : 'support@ivoriai.com';

  const sendEmail = async (subject, body) => {
    try {
      const token =
        sessionStorage.getItem('pathbuddy_access_token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('token') || '';
      const apiBase = getBaseApiUrl();
      await fetch(`${apiBase}/api/auth/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: userName || user?.name || 'User',
          email: userEmail || user?.email || (onPathbuddy ? 'unknown@pathbuddy.com' : 'unknown@ivoriai.com'),
          category: subject,
          message: body,
        }),
      });
    } catch (e) {
      // silently fail — the UX still shows success to avoid confusing users
      console.warn('Support email failed:', e);
    }
  };

  const handleContactSubmit = async (text) => {
    setContactSending(true);
    await sendEmail(`Contact the team – ${brandLabel}`, text);
    setContactSending(false);
    setContactSent(true);
  };

  const handleBugSubmit = async (text) => {
    setBugSending(true);
    await sendEmail(`Bug Report – ${brandLabel}`, text);
    setBugSending(false);
    setBugSent(true);
  };

  const openContact = () => { setIsProfileOpen(false); setContactSent(false); setContactOpen(true); };
  const openBug = () => { setIsProfileOpen(false); setBugSent(false); setBugOpen(true); };

  return (
    <>
      <div className={`user-profile-dropdown ${variant === 'sidebar' ? 'variant-sidebar' : ''}`} ref={dropdownRef}>

        {/* Pink Avatar Menu Trigger */}
        <button
          type="button"
          className="profile-avatar-trigger"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0', overflow: 'hidden' }}
          onClick={() => { setIsProfileOpen(!isProfileOpen); setIsAppsOpen(false); }}
          aria-expanded={isProfileOpen}
          aria-label="User profile"
        >
          <span className="avatar-initials">{displayInitials}</span>
        </button>

        {/* Profile settings dropdown */}
        {isProfileOpen && (
          <div className="dropdown-menu">
            <div className="dropdown-header">
              <div className="header-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <span className="avatar-initials">{displayInitials}</span>
              </div>
              <div className="header-info">
                <h4 className="header-name">{userName}</h4>
                <p className="header-email">{userEmail}</p>
              </div>
            </div>

            <div className="dropdown-content">
              {showInstructorDashboard && (
                <button className="dropdown-item" onClick={handleInstructorDashboardClick}>
                  <span className="item-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="9" rx="1" />
                      <rect x="14" y="3" width="7" height="5" rx="1" />
                      <rect x="14" y="12" width="7" height="9" rx="1" />
                      <rect x="3" y="16" width="7" height="5" rx="1" />
                    </svg>
                  </span>
                  <span className="item-label">Instructor dashboard</span>
                </button>
              )}

              {/* Settings */}
              <button className="dropdown-item" onClick={handleProfileClick}>
                <span className="item-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </span>
                <span className="item-label">Settings</span>
              </button>

              {/* Contact the team */}
              <button className="dropdown-item" onClick={openContact}>
                <span className="item-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </span>
                <span className="item-label">Contact the team</span>
              </button>

              {/* Report a bug */}
              <button className="dropdown-item" onClick={openBug}>
                <span className="item-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 6c0-2.21 1.79-4 4-4s4 1.79 4 4" />
                    <path d="M20 12H4" />
                    <path d="M12 12v8" />
                    <path d="M4 8l2 4" />
                    <path d="M20 8l-2 4" />
                    <rect x="8" y="12" width="8" height="8" rx="2" />
                  </svg>
                </span>
                <span className="item-label">Report a bug</span>
              </button>

              {/* Colour scheme: light, dark (ink) or black */}
              <div className="dropdown-item theme-picker-row">
                <span className="item-icon">
                  <ThemeModeIcon mode={theme} size={14} />
                </span>
                <span className="item-label" id="theme-picker-label">Theme</span>
                <span className="theme-picker" role="radiogroup" aria-labelledby="theme-picker-label">
                  {THEMES.map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={theme === mode}
                      className={`theme-picker-option${theme === mode ? ' is-active' : ''}`}
                      onClick={() => setTheme(mode)}
                    >
                      {THEME_LABELS[mode]}
                    </button>
                  ))}
                </span>
              </div>

              {/* Cookie choices: reopens the consent panel (withdrawing is as easy as giving) */}
              <button
                className="dropdown-item"
                onClick={() => { setIsProfileOpen(false); openCookieSettings(); }}
              >
                <span className="item-icon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M8.5 9h.01M15 8h.01M10 14h.01M15 14.5h.01" />
                  </svg>
                </span>
                <span className="item-label">Cookie settings</span>
              </button>

              <div className="dropdown-divider" style={{ height: '1px', background: 'var(--iv-bg)', margin: '4px 0' }} />

              {/* Sign In / Sign Out based on auth state */}
              {user ? (
                <button className="dropdown-item logout-item" onClick={handleLogout}>
                  <span className="item-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="item-label">Sign Out</span>
                </button>
              ) : (
                <button className="dropdown-item" onClick={() => { setIsProfileOpen(false); navigate('/login'); }} style={{ color: 'var(--iv-accent)', fontWeight: '600' }}>
                  <span className="item-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                      <polyline points="10 17 15 12 10 7"/>
                      <line x1="15" y1="12" x2="3" y2="12"/>
                    </svg>
                  </span>
                  <span className="item-label">Sign In / Sign Up</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Contact the team modal */}
      <InlineModal
        isOpen={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Talk to our team"
        subtitle={`Need support, or have a question? We'll get back to you at ${supportEmail}.`}
        placeholder="How can we help? Tell the team what you need…"
        buttonLabel="Send message"
        onSubmit={handleContactSubmit}
        sending={contactSending}
        sent={contactSent}
        extraContent={
          <div className="upd-modal-direct-email">
            Or email us directly:
            <a href={`mailto:${supportEmail}`} className="upd-modal-email-link">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              {supportEmail}
            </a>
          </div>
        }
      />

      {/* Report a bug modal */}
      <InlineModal
        isOpen={bugOpen}
        onClose={() => setBugOpen(false)}
        title="Send a bug report"
        subtitle="Your report goes straight to our team."
        placeholder="What went wrong? Describe the issue…"
        buttonLabel="Send report"
        onSubmit={handleBugSubmit}
        sending={bugSending}
        sent={bugSent}
      />
    </>
  );
};

export default UserProfileDropdown;